import { useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Bell, Calendar, Clock, MapPin, Video } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';

interface MeetingReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  meeting: {
    id: string;
    title: string;
    scheduled_at: string;
    location?: string | null;
    meeting_url?: string | null;
    google_calendar_html_link?: string | null;
  } | null;
}

export function MeetingReminderModal({ isOpen, onClose, meeting }: MeetingReminderModalProps) {
  useEffect(() => {
    if (isOpen) {
      // Play notification sound
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZUQ8PVqvn77BdGAg+ltryxnMjBSuBzvLZijcIGWi77OifTRAMUKfj8LZjHAY4ktjyzXosBS+A0fLaizkHH2vB7+iYUQ8OV6rm8LFcGQlBnNvyxHQkBi+Cyc/bizmGH2nD8OScTRILV6rm8LFcGQlBnNvyxHQkBi+Cyc/bizmGH2nD8OScTRILV6rm8LFcGQlBnNvyxHQkBi+Cyc/bizmGH2nD8OScTRILV6rm8LFcGQlBnNvyxHQkBi+Cyc/bizmGH2nD8OScTRIL');
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Silently fail if audio doesn't play
      });
    }
  }, [isOpen]);

  if (!meeting) return null;

  const scheduledDate = new Date(meeting.scheduled_at);
  const formattedTime = format(scheduledDate, "HH:mm", { locale: ptBR });
  const formattedDate = format(scheduledDate, "dd/MM/yyyy", { locale: ptBR });
  
  const meetingLink = meeting.meeting_url || meeting.google_calendar_html_link;

  const handleOpenMeeting = () => {
    if (meetingLink) {
      window.open(meetingLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent 
        className="max-w-md border-2 border-orange-300" 
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <AlertDialogHeader>
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-orange-100 animate-pulse">
            <Bell className="w-6 h-6 text-orange-600" />
          </div>
          <AlertDialogTitle className="text-center text-xl">
            🔔 Sua Reunião Começa em 5 Minutos!
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4 pt-4">
              <div className="p-4 rounded-lg bg-orange-50 border border-orange-200 space-y-3">
                <div>
                  <h4 className="font-semibold text-foreground mb-1 text-base">{meeting.title}</h4>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="text-foreground font-medium">{formattedTime} - {formattedDate}</span>
                </div>
                
                {meeting.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-orange-600" />
                    <span className="text-foreground">{meeting.location}</span>
                  </div>
                )}
                
                {meetingLink && (
                  <div className="flex items-start gap-2 text-sm pt-2 border-t border-orange-200">
                    <Video className="w-4 h-4 text-orange-600 mt-0.5" />
                    <span className="text-foreground text-xs break-all">{meetingLink}</span>
                  </div>
                )}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-col gap-2">
          {meetingLink && (
            <Button
              onClick={handleOpenMeeting}
              className="w-full bg-orange-600 hover:bg-orange-700"
            >
              <Video className="w-4 h-4 mr-2" />
              Abrir Reunião
            </Button>
          )}
          <AlertDialogAction onClick={onClose} className="w-full" variant="outline">
            OK, Estou Ciente
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
